import { useRef, useCallback, useEffect, useState } from 'react';

export type PreviewState = 'idle' | 'building' | 'ready' | 'error';
type ViewportMode = 'responsive' | 'desktop' | 'tablet' | 'mobile';

const VIEWPORT_WIDTHS: Record<ViewportMode, number | null> = {
  responsive: null,
  desktop: 1280,
  tablet: 768,
  mobile: 375,
};

// Device frame native sizes: outer dimensions and inner screen width
const DEVICE_FRAME = {
  mobile: { outerW: 414, outerH: 914, screenW: 390 },
  tablet: { outerW: 792, outerH: 1058, screenW: 768 },
} as const;

const BUILD_STEPS = [
  { at: 0,     label: 'Starting build…' },
  { at: 0.25,  label: 'Fetching content…' },
  { at: 0.57,  label: 'Compiling site…' },
  { at: 0.89,  label: 'Almost ready…' },
];

// Build duration history stored in localStorage
const BUILD_HISTORY_KEY = 'topzite:buildTimes';
const DEFAULT_EXPECTED_MS = 28000;

function loadBuildHistory(): number[] {
  try { return JSON.parse(localStorage.getItem(BUILD_HISTORY_KEY) ?? '[]'); }
  catch { return []; }
}

function recordBuildTime(ms: number) {
  const h = loadBuildHistory();
  h.push(ms);
  if (h.length > 6) h.shift();
  localStorage.setItem(BUILD_HISTORY_KEY, JSON.stringify(h));
}

function getExpectedMs(): number {
  const h = loadBuildHistory();
  if (!h.length) return DEFAULT_EXPECTED_MS;
  const sorted = [...h].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
}

function getScaledSteps(expectedMs: number) {
  return BUILD_STEPS.map(s => ({ label: s.label, at: Math.round(s.at * expectedMs) }));
}

const STATE_DOT_COLOR: Record<PreviewState, string> = {
  idle: 'var(--color-gray-300)',
  building: '#f59e0b',
  ready: '#22c55e',
  error: 'var(--color-error)',
};

interface PreviewPanelProps {
  subdomain: string | null;
  currentPage?: string;
  state: PreviewState;
  errorMsg: string | null;
  cacheBust: number;
  onTriggerPreview: () => void;
  onRefreshIframe: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Optional external ref for the iframe — enables parent to postMessage directly */
  iframeRef?: React.MutableRefObject<HTMLIFrameElement | null>;
  /** When true, auto-trigger a preview build on mount if state is idle */
  autoTrigger?: boolean;
  /** When true, show a hint that new items need a rebuild to appear */
  isNewContent?: boolean;
  /** Called when viewport mode changes — parent can resize panel accordingly */
  onViewportChange?: (mode: ViewportMode, targetWidth: number | null) => void;
  /** Called after iframe loads — used to replay queued preview updates */
  onIframeLoad?: () => void;
  /** When true, shows an "Unsaved" badge — preview reflects unsaved form state */
  previewDirty?: boolean;
}

export default function PreviewPanel({
  subdomain,
  currentPage = '/',
  state,
  errorMsg,
  cacheBust,
  onTriggerPreview,
  onRefreshIframe,
  collapsed,
  onToggleCollapsed,
  iframeRef: externalIframeRef,
  autoTrigger = false,
  isNewContent = false,
  onViewportChange,
  onIframeLoad,
  previewDirty = false,
}: PreviewPanelProps) {
  const internalIframeRef = useRef<HTMLIFrameElement>(null);
  const previewBodyRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<ViewportMode>('responsive');
  const [buildStepIndex, setBuildStepIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [builtIn, setBuiltIn] = useState<number | null>(null);
  const buildStartRef = useRef<number | null>(null);

  const handleViewportChange = useCallback((mode: ViewportMode) => {
    setViewport(mode);
    onViewportChange?.(mode, VIEWPORT_WIDTHS[mode]);
  }, [onViewportChange]);

  // Animate through build steps using history-calibrated timings
  useEffect(() => {
    if (state !== 'building') {
      setBuildStepIndex(0);
      return;
    }
    const steps = getScaledSteps(getExpectedMs());
    const timers = steps.slice(1).map(({ at }, i) =>
      setTimeout(() => setBuildStepIndex(i + 1), at)
    );
    return () => timers.forEach(clearTimeout);
  }, [state]);

  // Track elapsed time and record duration on completion
  useEffect(() => {
    if (state === 'building') {
      buildStartRef.current = Date.now();
      setElapsed(0);
      const timer = setInterval(() => {
        setElapsed(Math.floor((Date.now() - (buildStartRef.current ?? Date.now())) / 1000));
      }, 1000);
      return () => clearInterval(timer);
    }
    if (state === 'ready' && buildStartRef.current) {
      const ms = Date.now() - buildStartRef.current;
      buildStartRef.current = null;
      recordBuildTime(ms);
      const secs = Math.round(ms / 1000);
      setBuiltIn(secs);
      const t = setTimeout(() => setBuiltIn(null), 3000);
      return () => clearTimeout(t);
    }
  }, [state]);

  // Compute scale factor for desktop mode based on panel width
  const [desktopScale, setDesktopScale] = useState(0.4);
  useEffect(() => {
    if (viewport !== 'desktop' || !previewBodyRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const panelWidth = entry.contentRect.width;
        const scale = Math.min(panelWidth / 1280, 1);
        setDesktopScale(scale);
      }
    });
    observer.observe(previewBodyRef.current);
    const panelWidth = previewBodyRef.current.clientWidth;
    setDesktopScale(Math.min(panelWidth / 1280, 1));
    return () => observer.disconnect();
  }, [viewport]);

  // Compute scale factor for device frame modes (mobile / tablet)
  const [frameScale, setFrameScale] = useState(0.65);
  useEffect(() => {
    if ((viewport !== 'mobile' && viewport !== 'tablet') || !previewBodyRef.current) return;
    const frame = DEVICE_FRAME[viewport as 'mobile' | 'tablet'];
    const compute = (el: HTMLDivElement) => {
      const { width } = el.getBoundingClientRect();
      setFrameScale(Math.min((width - 16) / frame.outerW, 1));
    };
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) compute(entry.target as HTMLDivElement);
    });
    observer.observe(previewBodyRef.current);
    compute(previewBodyRef.current);
    return () => observer.disconnect();
  }, [viewport]);

  // Replay queued messages after iframe loads (with small delay for overlay init)
  const handleIframeLoad = useCallback(() => {
    if (onIframeLoad) {
      setTimeout(onIframeLoad, 200);
    }
  }, [onIframeLoad]);

  // Merge external and internal refs
  const setIframeRef = useCallback(
    (el: HTMLIFrameElement | null) => {
      (internalIframeRef as React.MutableRefObject<HTMLIFrameElement | null>).current = el;
      if (externalIframeRef) {
        externalIframeRef.current = el;
      }
    },
    [externalIframeRef]
  );

  // Auto-trigger preview build on mount when idle
  useEffect(() => {
    if (autoTrigger && state === 'idle' && !collapsed) {
      onTriggerPreview();
    }
    // Only run once on mount (or when state transitions to idle with autoTrigger)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTrigger]);

  const [copied, setCopied] = useState(false);

  const previewBaseUrl = subdomain
    ? `https://${subdomain}.topzite.net/_preview`
    : null;

  const pagePath = currentPage === '/' ? '/' : currentPage;
  const previewUrl = previewBaseUrl
    ? `${previewBaseUrl}${pagePath}${cacheBust ? `?_cb=${cacheBust}` : ''}`
    : null;
  const shareableUrl = previewBaseUrl ? `${previewBaseUrl}${pagePath}` : null;

  const handleCopyLink = useCallback(() => {
    if (!shareableUrl) return;
    navigator.clipboard.writeText(shareableUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [shareableUrl]);

  const handleOpenNewTab = () => {
    if (previewUrl) {
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (collapsed) {
    return (
      <div className="preview-panel collapsed">
        <button
          className="preview-panel-toggle"
          onClick={onToggleCollapsed}
          title="Show preview"
        >
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
          <span
            className="preview-panel-toggle-dot"
            style={{ background: STATE_DOT_COLOR[state] }}
            title={state === 'building' ? 'Building…' : state === 'ready' ? 'Ready' : state === 'error' ? 'Build failed' : 'No preview'}
          />
        </button>
      </div>
    );
  }


  return (
    <div className="preview-panel">
      <div className="preview-panel-header">
        <span className="preview-panel-title">
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
          {' '}Preview
          {previewDirty && <span className="preview-panel-unsaved">Unsaved</span>}
          {builtIn !== null && <span className="preview-panel-built-time">Built in {builtIn}s</span>}
        </span>
        <div className="preview-panel-header-actions">
          {/* Viewport toggles */}
          {state === 'ready' && (
            <div className="preview-viewport-toggles">
              <button
                className={`preview-panel-btn${viewport === 'responsive' ? ' preview-panel-btn--active' : ''}`}
                onClick={() => handleViewportChange('responsive')}
                title="Responsive (fill panel)"
              >
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
              </button>
              <button
                className={`preview-panel-btn${viewport === 'desktop' ? ' preview-panel-btn--active' : ''}`}
                onClick={() => handleViewportChange('desktop')}
                title="Desktop (1280px, scaled)"
              >
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                  <line x1="2" y1="20" x2="22" y2="20"/>
                </svg>
              </button>
              <button
                className={`preview-panel-btn${viewport === 'tablet' ? ' preview-panel-btn--active' : ''}`}
                onClick={() => handleViewportChange('tablet')}
                title="Tablet (768px)"
              >
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="2" width="16" height="20" rx="2" ry="2"/>
                  <line x1="12" y1="18" x2="12" y2="18"/>
                </svg>
              </button>
              <button
                className={`preview-panel-btn${viewport === 'mobile' ? ' preview-panel-btn--active' : ''}`}
                onClick={() => handleViewportChange('mobile')}
                title="Mobile (375px)"
              >
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                  <line x1="12" y1="18" x2="12" y2="18"/>
                </svg>
              </button>
            </div>
          )}
          <button
            className="preview-panel-btn preview-panel-btn--rebuild"
            onClick={onTriggerPreview}
            disabled={state === 'building'}
            title="Full rebuild — regenerates the site (20–30s)"
          >
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/>
              <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
            </svg>
            <span>Rebuild</span>
          </button>
          {state === 'ready' && shareableUrl && (
            <button
              className={`preview-panel-btn${copied ? ' preview-panel-btn--copied' : ''}`}
              onClick={handleCopyLink}
              title="Copy shareable preview link"
            >
              {copied ? (
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              ) : (
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                </svg>
              )}
            </button>
          )}
          {state === 'ready' && previewUrl && (
            <button
              className="preview-panel-btn"
              onClick={handleOpenNewTab}
              title="Open in new tab"
            >
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                <polyline points="15 3 21 3 21 9"/>
                <line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
            </button>
          )}
          <button
            className="preview-panel-btn"
            onClick={onToggleCollapsed}
            title="Hide preview"
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      </div>

      {state === 'ready' && previewUrl && (
        <div className="preview-panel-toolbar">
          <span className="preview-panel-url" title={previewUrl}>
            {subdomain}.topzite.net{pagePath}
          </span>
          <button
            className="preview-panel-btn-sm"
            onClick={onRefreshIframe}
            title="Reload iframe (instant — does not rebuild)"
          >
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/>
              <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
            </svg>
          </button>
        </div>
      )}

      <div className="preview-panel-body" ref={previewBodyRef}>
        {state === 'idle' && (
          <div className="preview-panel-empty">
            <svg width={32} height={32} viewBox="0 0 24 24" fill="none" stroke="var(--color-gray-300)" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
            <p>No preview yet</p>
            <button className="btn btn-secondary btn-sm" onClick={onTriggerPreview}>
              Build Preview
            </button>
          </div>
        )}

        {state === 'building' && (
          <div className="preview-panel-building">
            <div className="spinner" />
            <p>Building Preview</p>
            <div className="preview-panel-build-steps">
              {BUILD_STEPS.map((step, i) => (
                <div
                  key={step.at}
                  className={`preview-panel-build-step${i <= buildStepIndex ? ' active' : ''}`}
                />
              ))}
            </div>
            <p className="preview-panel-building-hint">
              ~{Math.round(getExpectedMs() / 1000)}s &middot; {elapsed}s elapsed
            </p>
          </div>
        )}

        {state === 'error' && (
          <div className="preview-panel-error">
            <p>{errorMsg || 'Preview build failed'}</p>
            <button className="btn btn-secondary btn-sm" onClick={onTriggerPreview}>
              Try Again
            </button>
          </div>
        )}

        {state === 'ready' && previewUrl && (
          <>
            {isNewContent && (
              <div className="preview-panel-new-content">
                <span>New content added — rebuild to see it in preview.</span>
                <button
                  className="preview-panel-new-content-btn"
                  onClick={onTriggerPreview}
                >
                  Rebuild now
                </button>
              </div>
            )}
            {viewport === 'desktop' ? (
              <div
                className="preview-iframe-desktop-scaler"
                style={{ '--preview-scale': desktopScale } as React.CSSProperties}
              >
                <iframe
                  ref={setIframeRef}
                  src={previewUrl}
                  className="preview-panel-iframe"
                  style={{ width: 1280 }}
                  title="Site preview"
                  onLoad={handleIframeLoad}
                />
              </div>
            ) : (viewport === 'mobile' || viewport === 'tablet') ? (
              <div className="preview-device-scaler">
                <div
                  className={`preview-device-frame preview-device-frame--${viewport}`}
                  style={{ zoom: frameScale }}
                >
                  <div className="preview-device-notch" />
                  <div className="preview-device-screen">
                    <iframe
                      ref={setIframeRef}
                      src={previewUrl}
                      className="preview-panel-iframe"
                      style={{ width: DEVICE_FRAME[viewport].screenW }}
                      title="Site preview"
                      onLoad={handleIframeLoad}
                    />
                  </div>
                  {viewport === 'mobile' && <div className="preview-device-home" />}
                </div>
              </div>
            ) : (
              <div className="preview-iframe-wrapper">
                <iframe
                  ref={setIframeRef}
                  src={previewUrl}
                  className="preview-panel-iframe"
                  title="Site preview"
                  onLoad={handleIframeLoad}
                />
              </div>
            )}
          </>
        )}

        {state === 'ready' && !previewUrl && (
          <div className="preview-panel-empty">
            <p>No subdomain configured</p>
          </div>
        )}
      </div>
    </div>
  );
}
